import {
  GET_STARTED_ACTIONS,
  GetStartedAction,
  GetStartedChoice,
  resolveProUrl,
} from "../GetStartedScreen";

// ACA-266, the fourth v2 screen (design: App Onboarding.dc.html, the `isDone` step / TASKS.gallery).
// The artist flow's GetStartedScreen.tsx already builds this screen end to end - same greeting,
// same "What would you like to do first?", same fixed-order-no-reordering rule (ACA-37) - it is
// just Mantine, and the v2 screens stay off Mantine for the reason GalleryPracticeStep.tsx
// documents (no brand theme arm in mkt; the design is all custom controls anyway). So this file
// only rebuilds the *rendering*. The action shape and resolveProUrl are imported rather than
// re-declared, and the two destinations this list shares with the artist one (inventory, the site
// theme, the product home) are read off GET_STARTED_ACTIONS instead of retyped - a destination
// can only have one source, or the two drift.
//
// The gallery list differs from the artist one at exactly one seat (TASKS.gallery in the design):
// "Invite an artist to consign" takes the second slot, and "Set up your site theme" moves down to
// make room. `recommended` is `i === 0` on this fixed list - the same rule the design writes for
// every persona - and is computed from array position at render time, never stored on the data or
// taken from a prop. There is deliberately no goals-shaped input to this component at all: the
// surest way to guarantee the badge can't follow which goals were picked is for the component to
// have no way of seeing them, which a prop-and-ignore-it approach only pretends to guarantee.

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m10 6 6 6-6 6" />
  </svg>
);

const ArtworkTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.5 7.6 12 3.2l8.5 4.4v8.8L12 20.8l-8.5-4.4z" />
    <path d="M3.5 7.6 12 12l8.5-4.4M12 12v8.8" />
  </svg>
);

const ConsignTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3.6 11.4V4.6a1 1 0 0 1 1-1h6.8l8.6 8.6-7.8 7.8z" />
    <circle cx="7.9" cy="7.9" r="1.2" />
  </svg>
);

const SiteTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="8.6" />
    <path d="M3.4 12h17.2M12 3.4c2.2 2.4 3.4 5.4 3.4 8.6s-1.2 6.2-3.4 8.6c-2.2-2.4-3.4-5.4-3.4-8.6S9.8 5.8 12 3.4z" />
  </svg>
);

const ExploreTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 16l2-6 6-2-2 6z" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);

// ACA-267: the artist list's "Add your contact(s)" row had no v2 icon before this file's own
// ARTIST_GET_STARTED_ACTIONS needed one - the legacy checklist's own contact glyph is Mantine, the
// era this file's icons deliberately left behind.
const ContactTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8.4" r="3.4" />
    <path d="M4.8 20c0-3.9 3.2-6.8 7.2-6.8s7.2 2.9 7.2 6.8" />
  </svg>
);

// ACA-267: "Complete your artist profile" needs a glyph of its own. ContactTaskIcon above is a bare
// head and shoulders and already means "Add your contact(s)" further down the same list, so this one
// frames the same figure in a card - a profile, not a person.
const ProfileTaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.4" />
    <circle cx="12" cy="10.4" r="2.4" />
    <path d="M8.2 16.6c0-2.1 1.7-3.6 3.8-3.6s3.8 1.5 3.8 3.6" />
  </svg>
);

/** Reads a destination off the artist list so the two screens never disagree about where it is. */
function destinationOf(key: string): string {
  const shared = GET_STARTED_ACTIONS.find((action) => action.key === key);
  if (!shared) {
    throw new Error(`GetStartedStep: expected GET_STARTED_ACTIONS to have a "${key}" entry`);
  }
  return shared.path;
}

// Fixed order, no goal-based reordering (ACA-266, the same rule ACA-37 gave the artist list) -
// TASKS.gallery in the design, read verbatim. Only "consign" has no artist-list counterpart to
// borrow a real destination from. The design does give every row an href, including this one
// ('Consignment Gallery.dc.html'), but all four are prototype cross-links to sibling mockup files,
// not real Pro hash routes - the same is true of the artist list's own design hrefs
// ('Artlogic Inventory.dc.html' etc.), and the real destinations that list uses (`inventory`,
// `website/design?selectTheme=1`, the empty product-home path) were never taken from those design
// hrefs either. So "artistInvites" below is not read off the design; it is this ticket's own
// choice, grounded in the dash's real route table (Route.ArtistInvites in Route.jsx, "Artist
// Invites" in the sidebar - the dedicated invite flow, not a filtered view of Consignments) rather
// than guessed from the mockup filename. Flagging it as a decision to register in the PR, not a
// fact the design settles.
export const GALLERY_GET_STARTED_ACTIONS: readonly GetStartedAction[] = [
  {
    key: "artwork",
    label: "Add your first artwork",
    path: destinationOf("artwork"),
    Icon: ArtworkTaskIcon,
  },
  {
    key: "consign",
    label: "Invite an artist to consign",
    path: "artistInvites",
    Icon: ConsignTaskIcon,
  },
  {
    key: "theme",
    label: "Set up your site theme",
    path: destinationOf("theme"),
    Icon: SiteTaskIcon,
  },
  {
    key: "explore",
    label: "Start exploring",
    path: destinationOf("explore"),
    Icon: ExploreTaskIcon,
  },
];

// ACA-267, the artist's fourth v2 screen - OnboardingV2.tsx hands this list to GetStartedStep's
// `actions` prop for the artist persona. It is the list the artist already sees today
// (GetStartedScreen.tsx's own GET_STARTED_ACTIONS) plus the design's "Complete your artist
// profile", restored on 28/09 after the PR review asked why it was gone. The 25/09 reasoning only
// ever covered the design's OTHER row, "Review your consignment invitation", which presumes an
// invitation this ticket has no evidence exists and stays out; the profile row had no reason of its
// own and was dropped with it. Its path is a literal because destinationOf borrows from the legacy
// list, which has no "profile" entry and throws when asked for one - the destination is the dash's
// own Route.Settings.Profile, this ticket's route choice the way "artistInvites" was the gallery's,
// not something the design settles. The remaining labels are the legacy list's verbatim, including
// its "Start Exploring" (capital E) - GALLERY_GET_STARTED_ACTIONS above spells it "Start exploring"
// and the two are left to disagree rather than silently harmonised. `recommended` is not carried
// over from the artist entry: the badge is decided by `index === 0` below, which the new row does
// not disturb.
export const ARTIST_GET_STARTED_ACTIONS: readonly GetStartedAction[] = [
  {
    key: "artwork",
    label: "Add your first artwork",
    path: destinationOf("artwork"),
    Icon: ArtworkTaskIcon,
  },
  {
    key: "profile",
    label: "Complete your artist profile",
    path: "settings/profile",
    Icon: ProfileTaskIcon,
  },
  {
    key: "theme",
    label: "Set up your site theme",
    path: destinationOf("theme"),
    Icon: SiteTaskIcon,
  },
  {
    key: "contacts",
    label: "Add your contact(s)",
    path: destinationOf("contacts"),
    Icon: ContactTaskIcon,
  },
  {
    key: "explore",
    label: "Start Exploring",
    path: destinationOf("explore"),
    Icon: ExploreTaskIcon,
  },
];

// ACA-268 (D5): the gallery's list without the consignment invite - filtered, not retyped, so the
// three rows that remain cannot drift from the gallery's.
export const COLLECTOR_GET_STARTED_ACTIONS: readonly GetStartedAction[] =
  GALLERY_GET_STARTED_ACTIONS.filter((action) => action.key !== "consign");

// The design's footer link (`homeHref`) lands on the same product home as "explore" - read off the
// same entry rather than repeat the empty-path literal a second time. Exported for OnboardingV2's
// own redirect (ACA-267 defect 2, B1): the module graph already runs OnboardingV2.tsx -> this file
// -> GetStartedScreen.tsx, so re-exporting this constant brings nothing new into it.
export const DASHBOARD_PATH = destinationOf("explore");

export interface GetStartedStepProps {
  firstName?: string;
  /** Optional hook fired before the row's native navigation (e.g. analytics). */
  onChoose?: (action: GetStartedChoice) => void;
  /**
   * ACA-267 Phase 5: the fixed list this screen renders. Defaults to GALLERY_GET_STARTED_ACTIONS,
   * which is what keeps every test in this file that does not pass one unchanged - the gallery is
   * still the persona this component was built for. ARTIST_GET_STARTED_ACTIONS (above) is the
   * artist's own fixed list, handed in by OnboardingV2.tsx rather than read off a table here, for
   * the same "no goals-shaped input" reason `recommended` is computed from array position: this
   * component has no way to see which persona is asking, on purpose.
   */
  actions?: readonly GetStartedAction[];
}

export function GetStartedStep({
  firstName,
  onChoose,
  actions = GALLERY_GET_STARTED_ACTIONS,
}: GetStartedStepProps) {
  const trimmedName = firstName?.trim();
  const heading = trimmedName ? `You're all set, ${trimmedName}.` : "You're all set.";

  return (
    <div className="get-started">
      <div className="get-started__intro">
        <span className="get-started__check">
          <CheckIcon />
        </span>
        <h1 className="onboarding-v2__heading" data-testid="screen-heading">
          {heading}
        </h1>
      </div>
      <p className="onboarding-v2__lead" data-testid="screen-subheading">
        What would you like to do first?
      </p>

      <nav className="get-started__tasks" aria-label="Get started actions">
        {actions.map((action, index) => {
          const { Icon } = action;
          const recommended = index === 0;
          const href = resolveProUrl(action.path);
          return (
            <a
              key={action.key}
              href={href}
              className="get-started__task"
              data-recommended={recommended ? "true" : "false"}
              data-testid={`get-started-${action.key}`}
              onClick={() => onChoose?.({ ...action, href })}
            >
              <span className="get-started__task-icon">
                <Icon />
              </span>
              <span className="get-started__task-label">{action.label}</span>
              {recommended && (
                <span className="get-started__badge" data-testid="recommended-badge">
                  Recommended
                </span>
              )}
              <span className="get-started__chevron">
                <ChevronIcon />
              </span>
            </a>
          );
        })}
      </nav>

      <p className="get-started__footer">
        <span>Not sure yet?</span>
        <a href={resolveProUrl(DASHBOARD_PATH)} data-testid="get-started-dashboard">
          Go to your dashboard
        </a>
      </p>
    </div>
  );
}
